"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useNotifications } from "../contexts/NotificationContext";
import type { Notification } from "../contexts/NotificationContext";

/* ==========================================================================
   CONSTANTS
   ========================================================================== */

const DEFAULT_TOAST_DURATION = 5000;
const MAX_VISIBLE_TOASTS = 3;
const SWIPE_THRESHOLD = 80; // px needed to trigger dismiss

/* ==========================================================================
   TOAST CONTAINER
   ========================================================================== */

/**
 * Renders toast popups in the top-right corner for new notifications.
 * Auto-dismisses after a configurable duration.
 */
export function NotificationToasts({ isDark }: { isDark: boolean }) {
  const { notifications, markAsRead } = useNotifications();
  const [visibleIds, setVisibleIds] = useState<Set<string>>(new Set());
  const [exitingIds, setExitingIds] = useState<Set<string>>(new Set());
  const timerRefs = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map()
  );
  const seenRef = useRef<Set<string>>(new Set());

  // Track new notifications and show them as toasts
  useEffect(() => {
    for (const notif of notifications) {
      if (seenRef.current.has(notif.id)) continue;
      seenRef.current.add(notif.id);

      setVisibleIds((prev) => {
        const next = new Set(prev);
        next.add(notif.id);
        return next;
      });

      const duration = notif.duration ?? DEFAULT_TOAST_DURATION;
      if (duration > 0) {
        const timer = setTimeout(() => {
          removeToast(notif.id);
        }, duration);
        timerRefs.current.set(notif.id, timer);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications]);

  // Cleanup timers on unmount
  useEffect(() => {
    const timers = timerRefs.current;
    return () => {
      for (const timer of timers.values()) clearTimeout(timer);
    };
  }, []);

  const removeToast = useCallback((id: string) => {
    // Start exit animation
    setExitingIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });

    // Remove after animation completes
    setTimeout(() => {
      setVisibleIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      setExitingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }, 300);

    const timer = timerRefs.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timerRefs.current.delete(id);
    }
  }, []);

  const handleToastClick = useCallback(
    (notif: Notification) => {
      markAsRead(notif.id);
      removeToast(notif.id);
      notif.onClick?.();
    },
    [markAsRead, removeToast]
  );

  const handleDismiss = useCallback(
    (e: React.MouseEvent, id: string) => {
      e.stopPropagation();
      removeToast(id);
    },
    [removeToast]
  );

  const dismissAllToasts = useCallback(() => {
    for (const id of visibleIds) removeToast(id);
  }, [visibleIds, removeToast]);

  const visibleToasts = notifications
    .filter((n) => visibleIds.has(n.id))
    .slice(0, MAX_VISIBLE_TOASTS);

  if (visibleToasts.length === 0) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 36,
        right: 8,
        zIndex: 200,
        display: "flex",
        flexDirection: "column",
        gap: 8,
        maxWidth: 360,
        width: "100%",
        pointerEvents: "none",
      }}
    >
      {visibleToasts.map((notif) => {
        const isExiting = exitingIds.has(notif.id);
        return (
          <SwipeableNotification
            key={notif.id}
            onDismiss={() => removeToast(notif.id)}
          >
            <div
              onClick={() => handleToastClick(notif)}
              style={{
                pointerEvents: "auto",
                cursor: notif.onClick ? "pointer" : "default",
                padding: "10px 14px",
                borderRadius: 10,
                backgroundColor: isDark
                  ? "rgba(40, 40, 40, 0.92)"
                  : "rgba(255, 255, 255, 0.92)",
                backdropFilter: "blur(20px)",
                WebkitBackdropFilter: "blur(20px)",
                border: isDark
                  ? "1px solid rgba(255,255,255,0.12)"
                  : "1px solid rgba(0,0,0,0.1)",
                boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
                transition: "all 0.3s ease",
                opacity: isExiting ? 0 : 1,
                transform: isExiting ? "translateX(120%)" : "translateX(0)",
                animation: isExiting ? undefined : "notif-slide-in 0.3s ease",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 10,
                }}
              >
                {/* Icon */}
                {notif.icon && (
                  <div style={{ flexShrink: 0, marginTop: 2 }}>
                    {notif.icon}
                  </div>
                )}
                {!notif.icon && (
                  <div
                    style={{
                      flexShrink: 0,
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      marginTop: 6,
                      backgroundColor: getTypeColor(notif.type),
                    }}
                  />
                )}

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  {notif.appName && (
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 500,
                        color: isDark
                          ? "rgba(255,255,255,0.5)"
                          : "rgba(0,0,0,0.45)",
                        marginBottom: 1,
                        textTransform: "uppercase",
                        letterSpacing: "0.03em",
                      }}
                    >
                      {notif.appName}
                    </div>
                  )}
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: isDark
                        ? "rgba(255,255,255,0.9)"
                        : "rgba(0,0,0,0.85)",
                      lineHeight: 1.3,
                    }}
                  >
                    {notif.title}
                  </div>
                  {notif.message && (
                    <div
                      style={{
                        fontSize: 12,
                        color: isDark
                          ? "rgba(255,255,255,0.6)"
                          : "rgba(0,0,0,0.55)",
                        marginTop: 2,
                        lineHeight: 1.4,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {notif.message}
                    </div>
                  )}
                </div>

                {/* Dismiss button */}
                <button
                  onClick={(e) => handleDismiss(e, notif.id)}
                  style={{
                    flexShrink: 0,
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    border: "none",
                    backgroundColor: isDark
                      ? "rgba(255,255,255,0.1)"
                      : "rgba(0,0,0,0.08)",
                    color: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.4)",
                    fontSize: 11,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    lineHeight: 1,
                    padding: 0,
                  }}
                >
                  ×
                </button>
              </div>
            </div>
          </SwipeableNotification>
        );
      })}

      {/* Dismiss all button — only when multiple toasts are visible */}
      {visibleToasts.length > 1 &&
        (() => {
          const allExiting = visibleToasts.every((n) => exitingIds.has(n.id));
          return (
            <button
              onClick={dismissAllToasts}
              style={{
                pointerEvents: allExiting ? "none" : "auto",
                alignSelf: "center",
                padding: "5px 14px",
                borderRadius: 14,
                border: isDark
                  ? "1px solid rgba(255,255,255,0.18)"
                  : "1px solid rgba(0,0,0,0.12)",
                fontSize: 11,
                fontWeight: 600,
                cursor: "pointer",
                backgroundColor: isDark
                  ? "rgba(40,40,42,0.78)"
                  : "rgba(255,255,255,0.85)",
                color: isDark ? "rgba(255,255,255,0.95)" : "rgba(0,0,0,0.85)",
                boxShadow: isDark
                  ? "0 2px 8px rgba(0,0,0,0.35)"
                  : "0 2px 8px rgba(0,0,0,0.12)",
                backdropFilter: "blur(20px) saturate(1.4)",
                WebkitBackdropFilter: "blur(20px) saturate(1.4)",
                opacity: allExiting ? 0 : 1,
                transform: allExiting ? "translateX(120%)" : "translateX(0)",
                transition:
                  "opacity 0.3s ease, transform 0.3s ease, background-color 0.15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = isDark
                  ? "rgba(60,60,62,0.88)"
                  : "rgba(255,255,255,0.95)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = isDark
                  ? "rgba(40,40,42,0.78)"
                  : "rgba(255,255,255,0.85)";
              }}
            >
              Dismiss all
            </button>
          );
        })()}

      {/* Keyframe animation injected via style tag */}
      <style>{`
        @keyframes notif-slide-in {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </div>
  );
}

/* ==========================================================================
   NOTIFICATION PANEL (slide-out)
   ========================================================================== */

export function NotificationPanel({
  isOpen,
  onClose,
  isDark,
}: {
  isOpen: boolean;
  onClose: () => void;
  isDark: boolean;
}) {
  const {
    notifications,
    dismissNotification,
    markAsRead,
    markAllAsRead,
    clearAll,
  } = useNotifications();
  const panelRef = useRef<HTMLDivElement>(null);

  // Close on click outside (but not when clicking the bell — let the bell toggle handle it)
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest("[data-notification-bell]")) return;
      if (panelRef.current && !panelRef.current.contains(target)) {
        onClose();
      }
    };
    // Delay to avoid catching the same click that opened the panel
    const timer = setTimeout(() => {
      document.addEventListener("mousedown", handler);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", handler);
    };
  }, [isOpen, onClose]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  const handleNotificationClick = useCallback(
    (notif: Notification) => {
      markAsRead(notif.id);
      notif.onClick?.();
      onClose();
    },
    [markAsRead, onClose]
  );

  if (!isOpen) return null;

  return (
    <div
      ref={panelRef}
      style={{
        position: "fixed",
        top: 32,
        right: 8,
        width: 360,
        maxHeight: "calc(100vh - 48px)",
        zIndex: 150,
        borderRadius: 12,
        backgroundColor: isDark
          ? "rgba(30, 30, 30, 0.95)"
          : "rgba(255, 255, 255, 0.95)",
        backdropFilter: "blur(24px)",
        WebkitBackdropFilter: "blur(24px)",
        border: isDark
          ? "1px solid rgba(255,255,255,0.12)"
          : "1px solid rgba(0,0,0,0.1)",
        boxShadow: "0 8px 40px rgba(0,0,0,0.3)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        animation: "notif-panel-in 0.2s ease",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 16px",
          borderBottom: isDark
            ? "1px solid rgba(255,255,255,0.08)"
            : "1px solid rgba(0,0,0,0.06)",
        }}
      >
        <span
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: isDark ? "rgba(255,255,255,0.9)" : "rgba(0,0,0,0.85)",
          }}
        >
          Notifications
        </span>
        <div style={{ display: "flex", gap: 8 }}>
          {notifications.length > 0 && (
            <>
              <button
                onClick={markAllAsRead}
                style={{
                  fontSize: 11,
                  color: "#007AFF",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "2px 4px",
                }}
              >
                Mark all read
              </button>
              <button
                onClick={clearAll}
                style={{
                  fontSize: 11,
                  color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.35)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "2px 4px",
                }}
              >
                Clear all
              </button>
            </>
          )}
        </div>
      </div>

      {/* Notification list */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "4px 0",
        }}
      >
        {notifications.length === 0 ? (
          <div
            style={{
              padding: "40px 16px",
              textAlign: "center",
              fontSize: 13,
              color: isDark ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.3)",
            }}
          >
            No notifications
          </div>
        ) : (
          notifications.map((notif) => (
            <SwipeableNotification
              key={notif.id}
              onDismiss={() => dismissNotification(notif.id)}
            >
              <div
                onClick={() => handleNotificationClick(notif)}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 10,
                  padding: "10px 16px",
                  cursor: notif.onClick ? "pointer" : "default",
                  backgroundColor: !notif.read
                    ? isDark
                      ? "rgba(0, 122, 255, 0.08)"
                      : "rgba(0, 122, 255, 0.05)"
                    : "transparent",
                  transition: "background-color 0.15s",
                }}
                onMouseEnter={(e) => {
                  if (notif.read) {
                    e.currentTarget.style.backgroundColor = isDark
                      ? "rgba(255,255,255,0.04)"
                      : "rgba(0,0,0,0.03)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = !notif.read
                    ? isDark
                      ? "rgba(0, 122, 255, 0.08)"
                      : "rgba(0, 122, 255, 0.05)"
                    : "transparent";
                }}
              >
                {/* Type dot or icon */}
                {notif.icon ? (
                  <div style={{ flexShrink: 0, marginTop: 2 }}>
                    {notif.icon}
                  </div>
                ) : (
                  <div
                    style={{
                      flexShrink: 0,
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      marginTop: 6,
                      backgroundColor: getTypeColor(notif.type),
                    }}
                  />
                )}

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      justifyContent: "space-between",
                      gap: 8,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                      }}
                    >
                      {notif.appName && (
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 500,
                            color: isDark
                              ? "rgba(255,255,255,0.4)"
                              : "rgba(0,0,0,0.35)",
                            textTransform: "uppercase",
                            letterSpacing: "0.03em",
                          }}
                        >
                          {notif.appName}
                        </span>
                      )}
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: notif.read ? 400 : 600,
                          color: isDark
                            ? "rgba(255,255,255,0.9)"
                            : "rgba(0,0,0,0.85)",
                        }}
                      >
                        {notif.title}
                      </span>
                    </div>
                    <span
                      style={{
                        fontSize: 10,
                        color: isDark
                          ? "rgba(255,255,255,0.3)"
                          : "rgba(0,0,0,0.25)",
                        flexShrink: 0,
                      }}
                    >
                      {formatTimeAgo(notif.timestamp)}
                    </span>
                  </div>
                  {notif.message && (
                    <div
                      style={{
                        fontSize: 12,
                        color: isDark
                          ? "rgba(255,255,255,0.55)"
                          : "rgba(0,0,0,0.5)",
                        marginTop: 2,
                        lineHeight: 1.4,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {notif.message}
                    </div>
                  )}
                </div>

                {/* Dismiss */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    dismissNotification(notif.id);
                  }}
                  style={{
                    flexShrink: 0,
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    border: "none",
                    backgroundColor: "transparent",
                    color: isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.2)",
                    fontSize: 13,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    lineHeight: 1,
                    padding: 0,
                    opacity: 0.6,
                    transition: "opacity 0.15s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = "1";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = "0.6";
                  }}
                >
                  ×
                </button>
              </div>
            </SwipeableNotification>
          ))
        )}
      </div>

      <style>{`
        @keyframes notif-panel-in {
          from {
            opacity: 0;
            transform: translateY(-8px) scale(0.97);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </div>
  );
}

/* ==========================================================================
   NOTIFICATION BELL (for MenuBar)
   ========================================================================== */

export function NotificationBell({
  isDark,
  onClick,
}: {
  isDark: boolean;
  onClick: () => void;
}) {
  const { unreadCount } = useNotifications();

  return (
    <button
      onClick={onClick}
      data-notification-bell
      title="Notifications"
      style={{
        position: "relative",
        background: "none",
        border: "none",
        cursor: "pointer",
        padding: "0 2px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: isDark ? "rgba(255,255,255,0.9)" : "rgba(0,0,0,0.85)",
      }}
    >
      {/* Bell SVG */}
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>

      {/* Badge */}
      {unreadCount > 0 && (
        <span
          style={{
            position: "absolute",
            top: -4,
            right: -4,
            minWidth: 14,
            height: 14,
            borderRadius: 7,
            backgroundColor: "#FF3B30",
            color: "#fff",
            fontSize: 9,
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "0 3px",
            lineHeight: 1,
          }}
        >
          {unreadCount > 99 ? "99+" : unreadCount}
        </span>
      )}
    </button>
  );
}

/* ==========================================================================
   SWIPEABLE NOTIFICATION WRAPPER
   ========================================================================== */

/**
 * Wraps a notification item with touch-swipe-to-dismiss (right or up).
 * On swipe past threshold, fades out and calls onDismiss.
 * Taps still fire onClick normally.
 */
function SwipeableNotification({
  onDismiss,
  children,
}: {
  onDismiss: () => void;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const touchState = useRef<{
    startX: number;
    startY: number;
    axis: "x" | "y" | null;
    swiping: boolean;
  } | null>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    touchState.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      axis: null,
      swiping: false,
    };
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const state = touchState.current;
    const el = ref.current;
    if (!state || !el) return;

    const touch = e.touches[0];
    const dx = touch.clientX - state.startX;
    const dy = state.startY - touch.clientY; // positive = up

    // Lock axis once we've moved enough
    if (!state.axis) {
      const absDx = Math.abs(dx);
      const absDy = Math.abs(touch.clientY - state.startY);
      if (absDx < 5 && absDy < 5) return; // deadzone
      state.axis = absDx > absDy ? "x" : "y";
    }

    if (state.axis === "x") {
      // Only allow right swipe (positive dx)
      const clampedDx = Math.max(0, dx);
      if (clampedDx > 0) {
        state.swiping = true;
        const progress = Math.min(clampedDx / SWIPE_THRESHOLD, 1);
        el.style.transform = `translateX(${clampedDx}px)`;
        el.style.opacity = String(1 - progress * 0.6);
        el.style.transition = "none";
      }
    } else {
      // Only allow up swipe (positive dy)
      const clampedDy = Math.max(0, dy);
      if (clampedDy > 0) {
        state.swiping = true;
        const progress = Math.min(clampedDy / SWIPE_THRESHOLD, 1);
        el.style.transform = `translateY(${-clampedDy}px)`;
        el.style.opacity = String(1 - progress * 0.6);
        el.style.transition = "none";
      }
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    const state = touchState.current;
    const el = ref.current;
    if (!state || !el) {
      touchState.current = null;
      return;
    }

    if (!state.swiping) {
      touchState.current = null;
      return;
    }

    // Check if we passed the threshold
    const transform = el.style.transform;
    const matchX = transform.match(/translateX\(([0-9.]+)px\)/);
    const matchY = transform.match(/translateY\(-([0-9.]+)px\)/);
    const distance = matchX
      ? parseFloat(matchX[1])
      : matchY
        ? parseFloat(matchY[1])
        : 0;

    if (distance >= SWIPE_THRESHOLD) {
      // Animate out fully then dismiss
      el.style.transition = "transform 0.2s ease, opacity 0.2s ease";
      if (matchX) {
        el.style.transform = "translateX(120%)";
      } else {
        el.style.transform = "translateY(-100%)";
      }
      el.style.opacity = "0";
      setTimeout(onDismiss, 200);
    } else {
      // Snap back
      el.style.transition = "transform 0.2s ease, opacity 0.2s ease";
      el.style.transform = "translateX(0) translateY(0)";
      el.style.opacity = "1";
    }

    touchState.current = null;
  }, [onDismiss]);

  return (
    <div
      ref={ref}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{ willChange: "transform, opacity" }}
    >
      {children}
    </div>
  );
}

/* ==========================================================================
   HELPERS
   ========================================================================== */

function getTypeColor(type: Notification["type"]): string {
  switch (type) {
    case "success":
      return "#34C759";
    case "warning":
      return "#FF9500";
    case "error":
      return "#FF3B30";
    case "info":
    default:
      return "#007AFF";
  }
}

function formatTimeAgo(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return "now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}
